import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CHARGE_ITEM_LABELS } from "constants";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import profileService from "services/profileService";
import partnersService from "services/partnersService";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const ORGANIZATION_ID_FIELD = `business${"Entity"}Id`;
const MAX_COMMISSION_ROWS = 500;
const MAX_RATE_ROWS = 300;

const pageContent = (result) =>
  Array.isArray(result?.content) ? result.content : Array.isArray(result) ? result : [];

const lastSecondOfDay = (value) => dayjs(value).endOf("day");

const defaultDateRange = () => [
  dayjs().subtract(90, "day").startOf("day"),
  lastSecondOfDay(dayjs()),
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const money = (amount, currency = "USD") => {
  const numeric = Number(amount || 0);
  if (!Number.isFinite(numeric)) return `${currency} 0`;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  }).format(numeric);
};

const statusColor = (status) => {
  const value = String(status || "").toUpperCase();
  if (["ACTIVE", "APPROVED", "PAID", "SUCCESS", "COMPLETED"].includes(value)) return "green";
  if (["PENDING", "PROCESSING"].includes(value)) return "gold";
  if (["CANCELLED", "CANCELED", "FAILED", "REJECTED", "INACTIVE"].includes(value)) return "red";
  return "blue";
};

const partnerOrganizationId = (partner) =>
  partner?.organizationId || partner?.[ORGANIZATION_ID_FIELD] || "";

const commissionDate = (commission) =>
  commission?.createdAt || commission?.startAt || commission?.updatedAt || commission?.endAt || "";

const commissionCode = (commission) =>
  commission?.referralCode ||
  commission?.bgpCode ||
  commission?.businessGrowthPartnerCode ||
  commission?.partnerCode ||
  "";

const productLabel = (item) => {
  const value = String(item?.product || "").toUpperCase();
  if (value.includes("OVACUS")) return "Ovacus";
  if (value.includes("OLREADIA")) return "Olreadia";
  if (value.includes("OLITASK")) return "Olitask";
  return item?.product || "-";
};

const inRange = (value, range) => {
  if (!value) return false;
  const when = dayjs(value);
  if (!when.isValid()) return false;
  if (!range?.[0] || !range?.[1]) return true;
  return when.isSame(range[0]) || when.isSame(range[1]) || (when.isAfter(range[0]) && when.isBefore(range[1]));
};

const findCurrentPartner = ({ partners, organizations, actorId }) => {
  const organizationIds = new Set((organizations || []).map((organization) => organization.id).filter(Boolean));
  return (
    partners.find((item) => item?.partnerType === "USER" && item?.userId === actorId) ||
    partners.find(
      (item) =>
        item?.partnerType === "ORGANIZATION" &&
        organizationIds.has(partnerOrganizationId(item)),
    ) ||
    null
  );
};

const pageShellStyle = {
  minHeight: "100%",
  padding: 24,
  background: OLIVEEB_COLORS.bg,
};

const buttonBase = {
  borderRadius: 8,
  fontWeight: 700,
  boxShadow: `0 8px 18px ${OLIVEEB_COLORS.shadow}`,
};

const PartnerCommissions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const bgpCodeParam = searchParams.get("bgpCode") || "";
  const showRatesParam = searchParams.get("view") === "rates";

  const [loading, setLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [commissionRates, setCommissionRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [showRates, setShowRates] = useState(false);
  const [range, setRange] = useState(defaultDateRange);

  const loadCommissions = useCallback(async () => {
    if (!actorId) return;

    try {
      setLoading(true);
      const [organizationsResult, partnersResult] = await Promise.all([
        profileService.organizations.listForUser(actorId, { page: 0, size: 100 }),
        partnersService.list({ page: 0, size: 100 }),
      ]);
      const organizations = pageContent(organizationsResult);
      const partner = findCurrentPartner({
        partners: pageContent(partnersResult),
        organizations,
        actorId,
      });
      setSelectedPartner(partner);

      if (!partner?.id) {
        setCommissions([]);
        return;
      }

      const commissionsResult = await partnersService.listCommissions({
        businessGrowthPartnerId: partner.id,
        page: 0,
        size: MAX_COMMISSION_ROWS,
      });
      setCommissions(pageContent(commissionsResult));
    } catch (error) {
      message.error(error?.message || "Failed to load BGP commissions.");
    } finally {
      setLoading(false);
    }
  }, [actorId]);

  useEffect(() => {
    loadCommissions();
  }, [loadCommissions]);

  const loadCommissionRates = useCallback(async () => {
    try {
      setRatesLoading(true);
      const result = await partnersService.listCommissionRates({
        page: 0,
        size: MAX_RATE_ROWS,
      });
      const activeRates = pageContent(result).filter(
        (rate) => String(rate?.status || "").toUpperCase() === "ACTIVE",
      );
      setCommissionRates(activeRates);
      setShowRates(true);
    } catch (error) {
      message.error(error?.message || "Failed to load commission rates.");
    } finally {
      setRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showRatesParam) {
      loadCommissionRates();
    }
  }, [loadCommissionRates, showRatesParam]);

  const rangedCommissions = useMemo(
    () => commissions.filter((commission) => inRange(commissionDate(commission), range)),
    [commissions, range],
  );

  const visibleCommissions = useMemo(() => {
    if (!bgpCodeParam) return rangedCommissions;
    return rangedCommissions.filter(
      (commission) => String(commissionCode(commission)).toLowerCase() === bgpCodeParam.toLowerCase(),
    );
  }, [bgpCodeParam, rangedCommissions]);

  const totals = useMemo(() => {
    const percentageRecords = visibleCommissions.filter(
      (commission) => commission?.commissionValueType === "PERCENTAGE",
    );
    const commissionTotal = visibleCommissions.reduce(
      (total, commission) => total + Number(commission?.commissionAmount || 0),
      0,
    );

    return {
      count: visibleCommissions.length,
      commissionTotal,
      currency: visibleCommissions.find((commission) => commission?.currency)?.currency || "USD",
      percentageCount: percentageRecords.length,
      activeCount: visibleCommissions.filter((commission) =>
        ["ACTIVE", "APPROVED", "PAID", "PENDING"].includes(String(commission?.status || "").toUpperCase()),
      ).length,
    };
  }, [visibleCommissions]);

  const pageTitle = showRates ? "BGP Commission Rates" : "My BGP Commissions";
  const pageSubtitle = showRates
    ? "Review active Business Growth Partner commission rates."
    : "Review BGP commission records, totals, and the selected earning window.";

  const columns = [
    {
      title: "Product",
      width: 120,
      render: (_, commission) => (
        <Tag color="blue" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
          {productLabel(commission)}
        </Tag>
      ),
    },
    {
      title: "Charge",
      dataIndex: "chargeItemCode",
      width: 210,
      render: (value) => CHARGE_ITEM_LABELS[value] || value || "-",
    },
    {
      title: "BGP Code",
      width: 150,
      render: (_, commission) =>
        commissionCode(commission) ? (
          <Tag color="cyan" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
            {commissionCode(commission)}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Source Charge",
      width: 150,
      render: (_, commission) =>
        commission.sourceChargeAmount !== undefined && commission.sourceChargeAmount !== null ? (
          <Text>{money(commission.sourceChargeAmount, commission.currency || "USD")}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Rate",
      width: 120,
      render: (_, commission) =>
        commission.commissionValueType === "PERCENTAGE" ? (
          <Text strong style={{ color: OLIVEEB_COLORS.oliveebOrange }}>
            {commission.commissionRate || 0}%
          </Text>
        ) : (
          <Text type="secondary">Fixed</Text>
        ),
    },
    {
      title: "Commission Amount",
      width: 180,
      render: (_, commission) => (
        <Space direction="vertical" size={1}>
          <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
            {money(commission.commissionAmount, commission.currency || "USD")}
          </Text>
          <Text type="secondary">{commission.commissionValueType || "-"}</Text>
          <Text type="secondary">{commission.commissionType || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Scope",
      dataIndex: "commissionScope",
      width: 150,
      render: (value) => <Tag>{value || "-"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (value) => <Tag color={statusColor(value)}>{value || "-"}</Tag>,
    },
    {
      title: "Window",
      width: 260,
      render: (_, commission) => (
        <Space direction="vertical" size={1}>
          <Text>{formatDateTime(commission.startAt)}</Text>
          <Text type="secondary">{formatDateTime(commission.endAt)}</Text>
        </Space>
      ),
    },
    {
      title: "Created At",
      width: 190,
      render: (_, commission) => formatDateTime(commission.createdAt),
    },
  ];

  const rateColumns = [
    {
      title: "Product",
      width: 120,
      render: (_, rate) => (
        <Tag color="blue" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
          {productLabel(rate)}
        </Tag>
      ),
    },
    {
      title: "Charge",
      dataIndex: "chargeItemCode",
      width: 230,
      render: (value) => CHARGE_ITEM_LABELS[value] || value || "-",
    },
    {
      title: "Rate",
      width: 170,
      render: (_, rate) => (
        <Space direction="vertical" size={1}>
          <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
            {money(rate.commissionAmount, rate.currency || "USD")}
          </Text>
          {rate.commissionValueType === "PERCENTAGE" ? (
            <Text type="secondary">
              {rate.commissionRate || 0}% of {money(rate.principleAmount, rate.currency || "USD")}
            </Text>
          ) : (
            <Text type="secondary">Fixed amount</Text>
          )}
          <Text type="secondary">{rate.commissionType || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Scope",
      dataIndex: "commissionScope",
      width: 150,
      render: (value) => <Tag>{value || "-"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (value) => <Tag color={statusColor(value)}>{value || "-"}</Tag>,
    },
    {
      title: "Window",
      width: 260,
      render: (_, rate) => (
        <Space direction="vertical" size={1}>
          <Text>{formatDateTime(rate.startAt)}</Text>
          <Text type="secondary">{formatDateTime(rate.endAt)}</Text>
        </Space>
      ),
    },
    {
      title: "Created At",
      width: 190,
      render: (_, rate) => formatDateTime(rate.createdAt),
    },
  ];

  return (
    <div style={pageShellStyle}>
      <div
        style={{
          border: `1px solid ${OLIVEEB_COLORS.border}`,
          borderRadius: 8,
          padding: 18,
          marginBottom: 16,
          background: `linear-gradient(135deg, ${OLIVEEB_COLORS.white}, ${OLIVEEB_COLORS.greenSoft2})`,
          boxShadow: `0 14px 34px ${OLIVEEB_COLORS.shadow}`,
        }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col>
            <div>
              <Title level={3} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                {pageTitle}
              </Title>
              <Text style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                {pageSubtitle}
              </Text>
            </div>
          </Col>
          <Col>
            <Space wrap>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(paths.partners)}
                style={{
                  ...buttonBase,
                  background: OLIVEEB_COLORS.oliveebBlueSoft,
                  borderColor: OLIVEEB_COLORS.oliveebBlue,
                  color: OLIVEEB_COLORS.oliveebBlue,
                }}
              >
                Back
              </Button>
              <Button
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={loadCommissions}
                style={{
                  ...buttonBase,
                  background: OLIVEEB_COLORS.oliveebGreenSoft,
                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                  color: OLIVEEB_COLORS.oliveebGreen,
                }}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {!selectedPartner && !loading ? (
        <Card bordered={false} style={{ borderRadius: 8 }}>
          <Empty description="You do not have a BGP account yet.">
            <Button
              type="primary"
              onClick={() => navigate(paths.partners)}
              style={{
                ...buttonBase,
                background: OLIVEEB_COLORS.oliveebGreen,
                borderColor: OLIVEEB_COLORS.oliveebGreen,
              }}
            >
              Go to Partnerships
            </Button>
          </Empty>
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card bordered={false} style={{ borderRadius: 8 }}>
            <Row gutter={[14, 14]} align="middle">
              <Col xs={24} lg={10}>
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                  Commission Range
                </Text>
                <RangePicker
                  showTime
                  value={range}
                  onChange={(nextRange) => setRange(nextRange || defaultDateRange())}
                  style={{ width: "100%", marginTop: 8 }}
                  presets={[
                    { label: "Last 7 days", value: [dayjs().subtract(7, "day").startOf("day"), lastSecondOfDay(dayjs())] },
                    { label: "Last 30 days", value: [dayjs().subtract(30, "day").startOf("day"), lastSecondOfDay(dayjs())] },
                    { label: "Last 90 days", value: defaultDateRange() },
                    { label: "This month", value: [dayjs().startOf("month"), lastSecondOfDay(dayjs())] },
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} lg={3}>
                <Statistic
                  title="Records"
                  value={totals.count}
                  valueStyle={{ color: OLIVEEB_COLORS.oliveebBlue }}
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Statistic
                  title="Commission Total"
                  value={money(totals.commissionTotal, totals.currency)}
                  valueStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
                />
              </Col>
              <Col xs={24} sm={12} lg={3}>
                <Statistic
                  title="Percent Rules"
                  value={totals.percentageCount}
                  valueStyle={{ color: OLIVEEB_COLORS.oliveebOrange }}
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Statistic
                  title="Active/Pending"
                  value={totals.activeCount}
                  valueStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
                />
              </Col>
            </Row>
          </Card>

          {bgpCodeParam ? (
            <Card bordered={false} style={{ borderRadius: 8 }}>
              <Text>
                Showing commissions for BGP Code <Text code>{bgpCodeParam}</Text>.
              </Text>
            </Card>
          ) : null}

          {showRates ? (
            <Card
              title={<span style={{ color: OLIVEEB_COLORS.oliveebOrange }}>Active Commission Rates</span>}
              bordered={false}
              style={{ borderRadius: 8 }}
              extra={
                <Button
                  size="small"
                  onClick={() => setShowRates(false)}
                  style={{
                    ...buttonBase,
                    background: OLIVEEB_COLORS.white,
                    borderColor: OLIVEEB_COLORS.oliveebBlue,
                    color: OLIVEEB_COLORS.oliveebBlue,
                    boxShadow: "none",
                  }}
                >
                  Hide
                </Button>
              }
            >
              <Table
                rowKey={(record) => record.id || `${record.chargeItemCode}-${record.createdAt}`}
                loading={ratesLoading}
                columns={rateColumns}
                dataSource={commissionRates}
                locale={{ emptyText: "No active commission rates found." }}
                scroll={{ x: 1120 }}
                size="small"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `${total} active rates`,
                }}
              />
            </Card>
          ) : null}

          {!showRates ? (
            <Card
              title={<span style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Commission Details</span>}
              bordered={false}
              style={{ borderRadius: 8 }}
            >
              <Table
                rowKey={(record) => record.id || `${record.chargeItemCode}-${record.createdAt}`}
                loading={loading}
                columns={columns}
                dataSource={visibleCommissions}
                locale={{ emptyText: "No commissions found for this range." }}
                scroll={{ x: 1270 }}
                size="small"
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total) => `${total} commissions`,
                }}
              />
            </Card>
          ) : null}
        </Space>
      )}
    </div>
  );
};

export default PartnerCommissions;
